import React, { useState, useEffect, useCallback } from 'react';
import { NumericFormat } from 'react-number-format';
import type { BiddingInfo } from '../../../types';
import { NumericKeypad } from '../../common/NumericKeypad';
import { useAppDialog } from '../../../hooks/useAppDialog';

// 設定用Props
interface BiddingMinPriceSettingFormProps {
  item: BiddingInfo;
  onSubmit: (data: { itemId: string, branchNumber: number, bidMinPrice: number }) => void;
  isSaving?: boolean;
  isDisabled?: boolean;
}

// 最低落札値設定用コンポーネント
export const BiddingMinPriceSettingForm: React.FC<BiddingMinPriceSettingFormProps> = ({ 
  item, 
  onSubmit, 
  isSaving = false, 
  isDisabled = false 
}) => {
  //----------------
  // State定義
  //----------------
  // 初期値は item.bidMinPrice (最低落札値) を使用
  const [bidMinPrice, setBidMinPrice] = useState<number | ''>(item.bidMinPrice || '');
  // const [showKeypad, setShowKeypad] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { showConfirm, OverlayElement } = useAppDialog();

  //----------------
  // Effect定義
  //----------------
  // 親から渡されるitem情報が変わったらフォームも更新
  useEffect(() => {
    setBidMinPrice(item.bidMinPrice || '');
  }, [item.bidMinPrice]);
  // スマホ判定
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  //----------------
  // イベントハンドラー定義
  //----------------
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault(); 

    // 共通ダイアログを表示
    showConfirm({
      title: "価格設定確認",
      message: `最低落札値を「${Number(bidMinPrice).toLocaleString()}円」に設定しますか？`,
      onConfirm: () => {
        // OKが押されたときのみ onSubmit を実行
        onSubmit({
          itemId: item.itemId,
          branchNumber: item.branchNumber,
          bidMinPrice: Number(bidMinPrice) || 0,
        });
      }
    });
  }, [item.itemId, item.branchNumber, bidMinPrice, onSubmit, showConfirm]);

  
  // --- キーパッド用ロジック ---
  const handleNumClick = (num: string) => {
    const currentStr = bidMinPrice === '' ? '' : String(bidMinPrice);
    if (currentStr.length >= 7) return; // 7桁制限
    setBidMinPrice(Number(currentStr + num));
  };

  const handleDelete = () => {
    const currentStr = String(bidMinPrice);
    if (bidMinPrice === '' || currentStr.length <= 1) {
      setBidMinPrice('');
    } else {
      setBidMinPrice(Number(currentStr.slice(0, -1)));
    }
  };

  const handleClear = () => {
    setBidMinPrice('');
  };

  //----------------
  // その他定数定義
  //----------------
  const inputClasses = "mt-1 w-full p-2 border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500";
  // const labelClasses = "block text-sm font-medium text-slate-700";

  //----------------
  // レンダリング処理
  //----------------
  return (
    <div className="p-2 lg:p-4 bg-slate-50 h-full flex flex-col relative">
      
      {/* フックが提供する背景レイヤー */}
      {OverlayElement}
      
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">   
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
          
          {/* --- 最低落札値入力エリア --- */}
          <div className="form-group w-full md:col-span-1">
            
            {/* 【スマホ用】入力とボタンの一体型カード */}
            <div className="md:hidden mb-4 overflow-hidden rounded-xl border-2 border-indigo-600 bg-white shadow-lg">
              <div className="p-2">
                <NumericFormat
                  value={bidMinPrice === 0 ? '' : bidMinPrice}
                  thousandSeparator={true} 
                  allowNegative={false} 
                  decimalScale={0} 
                  placeholder="0"
                  inputMode="none" // キーパッドを使うので標準キーボードは出さない
                  readOnly={true} 
                  // 枠を消して数字を大きく強調
                  className="w-full border-none p-2 text-right text-3xl font-black text-slate-800 focus:ring-0"
                  isAllowed={(values) => values.value.length <= 7}
                />
              </div>
              
              <button 
                type="submit" 
                disabled={isSaving || isDisabled}
                // 下部にピタッとくっつくボタン
                className="w-full bg-indigo-600 py-4 text-xl font-bold text-white transition-all active:bg-indigo-700 active:scale-[0.98] disabled:bg-slate-300"
              >
                <span className="tracking-[0.3em] ml-[0.3em] whitespace-nowrap">
                  {isSaving ? '保存中...' : '価格決定'}
                </span>
              </button>
            </div>

            {/* 【PC用】従来の入力スタイル */}
            <div className="hidden md:block">
              <div className="relative w-full">
                <NumericFormat
                  value={bidMinPrice === 0 ? '' : bidMinPrice}
                  thousandSeparator={true} 
                  allowNegative={false} 
                  decimalScale={0} 
                  placeholder="0"
                  inputMode={isMobile ? "none" : "numeric"}
                  readOnly={isMobile}                       
                  className={`${inputClasses} text-right font-bold !text-2xl pr-4 h-12 tracking-wider`}   
                  onValueChange={(values) => {
                    if (!isMobile) setBidMinPrice(values.floatValue === undefined ? '' : values.floatValue);
                  }}
                  isAllowed={(values) => values.value.length <= 7}
                /> 
              </div>
            </div>          
          </div>
  
          {/* 【PC用】送信ボタン */}
          <div className="hidden md:flex md:col-start-3 justify-end items-end mb-2">
            <button 
              type="submit" 
              disabled={isSaving || isDisabled}
              className="w-48 py-2 text-base font-bold text-white bg-indigo-600 rounded-md shadow-sm hover:bg-indigo-700 active:scale-95 transition-all disabled:bg-slate-300"
            >
              {isSaving ? '保存中...' : '価　格　決　定'}
            </button>
          </div>
        </div>
      </form>

      {/* キーパッド (スマホのみ表示) */}
      <div className="lg:hidden flex-none bg-slate-200 border-t border-slate-200 pb-safe h-[320px]">
        <NumericKeypad 
          onNumClick={handleNumClick}
          onClear={handleClear}
          onDelete={handleDelete}
        />
      </div>

    </div>
  );
};